<template>
  <article
    class="c4-card"
    :class="{ head: head && showPos }"
    :data-tone="K.tone"
    :data-id="ticket.id"
    :data-kind="ticket.kind"
    :aria-label="label"
  >
    <div class="c4-band">
      <i class="mdi" :class="K.icon" aria-hidden="true"></i>
      <span class="c4-band-x">
        <span class="c4-band-t">{{ title }}</span>
        <span class="c4-band-s">{{ sub }}</span>
      </span>
      <span v-if="showPos" class="c4-pos" :class="{ n: !head }">{{ head ? nowLabel : pos }}</span>
    </div>

    <div v-if="hasPerson" class="c4-person">
      <span class="c4-av">
        <img v-if="photo" :src="photo" alt="" @error="photoFailed = true" />
        <template v-else>{{ initials }}</template>
      </span>
      <div class="c4-who">
        <h3 class="c4-name">{{ fullName }}</h3>
        <div v-if="user.company" class="c4-co">{{ user.company }}</div>
      </div>
      <div class="c4-chips">
        <span v-if="outcome.shift" class="c4-chip">
          <b>{{ outcome.shift }}.</b> {{ tx("shift") }}
        </span>
        <span class="c4-chip id">#{{ user.id }}</span>
      </div>
    </div>

    <div v-if="showItems" class="c4-items" :class="[itemsClass, { dim: ticket.kind === 'taken' }]">
      <div v-for="(it, i) in shownItems" :key="i" class="c4-it">
        <span class="c4-ph" :class="{ none: !imageOk(it) }">
          <img v-if="imageOk(it)" :src="imageUrl(it)" alt="" loading="lazy" @error="markFailed(it)" />
          <i v-else class="mdi mdi-image-off-outline" aria-hidden="true"></i>
        </span>
        <div class="c4-iname">
          {{ toLatin(it.name) }}<span v-if="adds(it)" class="c4-adds" data-fk="adds">: {{ adds(it) }}</span>
          <span v-if="it.note && it.note !== it.name" class="c4-inote">{{ toLatin(it.note) }}</span>
        </div>
        <span v-if="it.quantity > 1" class="c4-qty" :aria-label="it.quantity + ' ' + tx('pcs')">×{{ it.quantity }}</span>
      </div>
      <button
        v-if="restItems > 0"
        type="button"
        class="c4-more"
        data-fk="more"
        @click="$emit('items', ticket.id)"
      >
        {{ tx("items.more", { n: restItems }) }}
      </button>
    </div>

    <div v-else-if="why" class="c4-why" :class="{ sys }">
      <span class="c4-why-ic" aria-hidden="true"><i class="mdi" :class="why.icon"></i></span>
      <div>
        <h3>{{ why.title }}</h3>
        <div class="c4-why-p">
          {{ why.text }} <b v-if="why.bold">{{ why.bold }}</b>
        </div>
        <div v-if="why.server" class="c4-why-sv">{{ why.server }}</div>
        <span v-if="ticket.kind === 'unknown' && ticket.code" class="c4-chip id" style="margin-top: 10px">
          ID {{ ticket.code }}
        </span>
      </div>
    </div>

    <div v-if="pay" class="c4-pay" data-fk="pay">
      <i class="mdi mdi-cash-register" aria-hidden="true"></i>
      <span class="c4-pay-t">{{ tx("pay.collect") }}:</span> <b class="c4-pay-v">{{ pay }}</b>
    </div>

    <div v-if="hasFoot" class="c4-foot">
      <div v-if="foot.status" class="c4-fm" :class="foot.status.cls">
        <i class="mdi" :class="foot.status.icon" aria-hidden="true"></i>
        <span>{{ foot.status.text }}</span>
      </div>
      <div v-if="hasActs" class="c4-acts">
        <button v-if="foot.confirm" type="button" class="c4-btn green" data-fk="confirm" @click="$emit('confirm', ticket.id)">
          <i class="mdi mdi-check-bold" aria-hidden="true"></i>{{ tx("foot.confirm") }}
        </button>
        <button v-if="foot.retryConfirm" type="button" class="c4-btn brand" data-fk="reconfirm" @click="$emit('confirm', ticket.id)">
          <i class="mdi mdi-refresh" aria-hidden="true"></i>{{ tx("foot.retry") }}
        </button>
        <button v-if="foot.retryScan" type="button" class="c4-btn brand" data-fk="retry" @click="$emit('retry', ticket.id)">
          <i class="mdi mdi-refresh" aria-hidden="true"></i>{{ tx("foot.retry") }} · ID {{ ticket.code }}
        </button>
        <button v-if="foot.signin" type="button" class="c4-btn" data-fk="signin" @click="$emit('signin')">
          <i class="mdi mdi-login" aria-hidden="true"></i>{{ tx("why.signin") }}
        </button>
        <button v-if="foot.enterId" type="button" class="c4-btn brand" data-fk="enterid" @click="$emit('enter-id')">
          <i class="mdi mdi-keyboard-outline" aria-hidden="true"></i>{{ tx("why.enter_id") }}
        </button>
        <button v-if="foot.advance" type="button" class="c4-btn" data-fk="done" @click="$emit('advance')">
          {{ K.can ? tx("q.done") : tx("q.skip") }}<i class="mdi mdi-arrow-right" aria-hidden="true"></i>
        </button>
        <button v-if="foot.details" type="button" class="c4-link" data-fk="det" @click="$emit('details', ticket.id)">
          <i class="mdi mdi-format-list-checks" aria-hidden="true"></i>{{ tx("foot.details") }}
        </button>
      </div>
    </div>
  </article>
</template>

<script>
import conf from "@/config";
import canteenV4Text from "@/mixins/canteenV4Text";
import { KINDS } from "@/utils/canteenOutcome";
import { paymentText } from "@/utils/canteenPayment";

export default {
  name: "CanteenCard",
  mixins: [canteenV4Text],
  props: {
    ticket: { type: Object, required: true },
    // mjesto u redu usluživanja (1 = glavna)
    pos: { type: Number, default: 1 },
    head: { type: Boolean, default: false },
    // oznaka "Sad" i brojevi, samo kad je na ekranu više od jednog korisnika
    showPos: { type: Boolean, default: true },
    // glavna je najstariji u redu ("oldest") ili posljednji skenirani ("newest")
    order: { type: String, default: "oldest" },
    confirmAuto: { type: Boolean, default: true },
    restaurantId: { type: [Number, String], default: "" },
    // dugme "Detalji provjere" uz svaki ishod (podešavanje)
    showDetails: { type: Boolean, default: false },
  },
  data() {
    return { photoFailed: false, failedImages: {} };
  },
  computed: {
    K() {
      return KINDS[this.ticket.kind];
    },
    outcome() {
      return this.ticket.outcome;
    },
    user() {
      return this.outcome.user || {};
    },
    sys() {
      return ["error", "session", "unknown"].includes(this.ticket.kind);
    },
    hasPerson() {
      return !this.sys && !!this.outcome.user;
    },
    title() {
      return this.tx("o." + this.ticket.kind);
    },
    nowLabel() {
      return this.tx(this.order === "newest" ? "q.last" : "q.now");
    },
    confText() {
      const t = this.ticket;
      if (t.conf === "done") return `${this.tx("sub.done")} ${t.confAt}`;
      if (t.conf === "sending") return this.tx("sub.sending");
      if (t.conf === "pending") return this.tx("sub.pending");
      if (t.conf === "failed") return this.tx("sub.failed");
      return "";
    },
    sub() {
      const t = this.ticket;
      switch (t.kind) {
        case "ok":
          return this.confText;
        case "replacement":
          return [this.tx("sub.replacement"), this.confText].filter(Boolean).join(" · ");
        case "taken":
          return this.outcome.takenAt ? `${this.tx("taken.at")} ${this.outcome.takenAt}` : t.at;
        case "stop":
          return this.tx("sub.stop");
        case "unknown":
          return this.tx("sub.unknown");
        default:
          return t.at;
      }
    },
    fullName() {
      return `${this.toLatin(this.user.name)} ${this.toLatin(this.user.lastname)}`.trim();
    },
    initials() {
      const a = (this.user.name || "").trim().charAt(0);
      const b = (this.user.lastname || "").trim().charAt(0);
      return this.toLatin((a + b).toUpperCase());
    },
    photo() {
      if (this.photoFailed || !this.user.image_path) return "";
      return `${conf.apiUrlBase}/gallery/${this.user.image_path}`;
    },
    showItems() {
      return !this.sys && this.outcome.items.length > 0 && this.ticket.kind !== "stop";
    },
    // iznos za naplatu ("2650 DIN"), samo kad radnik plaća na kasi
    pay() {
      return this.showItems ? paymentText(this.outcome.pay, this.$i18n.locale) : "";
    },
    itemsClass() {
      const n = this.outcome.items.length;
      return n <= 1 ? "n1" : n <= 4 ? "n3" : "nn";
    },
    shownItems() {
      const its = this.outcome.items;
      const max = this.itemsClass === "nn" ? 6 : 4;
      return its.length > max ? its.slice(0, max - 1) : its;
    },
    restItems() {
      return this.outcome.items.length - this.shownItems.length;
    },
    why() {
      const t = this.ticket;
      const server = this.outcome.text ? this.translate(this.outcome.text) : "";
      if (t.kind === "stop") {
        return {
          icon: "mdi-close-octagon",
          title: this.tx("foot.noserve"),
          text: this.outcome.knownReason || !server ? this.tx("why.stop") : server,
          bold: this.tx("why.stop_next"),
        };
      }
      if (t.kind === "unknown") {
        return { icon: "mdi-account-question-outline", title: this.tx("why.unknown"), text: this.tx("why.unknown_sub") };
      }
      if (t.kind === "error") {
        return {
          icon: "mdi-wifi-off",
          title: this.tx("why.error"),
          text: t.err && t.err.timeout ? this.tx("why.error_timeout") : this.tx("why.error_sub"),
        };
      }
      if (t.kind === "session") {
        return { icon: "mdi-lock-clock", title: this.tx("why.session"), text: this.tx("why.session_sub") };
      }
      return null;
    },
    // STOP: razlog je u objašnjenju; stavke se ne pokazuju
    canAdvance() {
      if (!this.head || !this.showPos || this.ticket.kind === "session") return false;
      return !this.K.can || this.ticket.conf === "done";
    },
    foot() {
      const t = this.ticket;
      const f = { status: null };
      if (this.K.can) {
        if (t.conf === "sending") f.status = { cls: "wait", icon: "mdi-cached c4-spin", text: this.tx("foot.sending") };
        else if (t.conf === "failed") f.status = { cls: "bad", icon: "mdi-alert-circle-outline", text: this.tx("foot.failed") };
        else if (t.conf === "done") {
          f.status = {
            cls: "ok",
            icon: "mdi-check-circle-outline",
            text: t.how === "manual" || !this.confirmAuto ? this.tx("foot.done") : this.tx("foot.auto"),
          };
        } else if (t.conf === "pending") f.status = { cls: "wait", icon: "mdi-timer-sand", text: this.tx("sub.pending") };
      } else if (t.kind === "taken") {
        f.status = { cls: "bad", icon: "mdi-close-octagon", text: `${this.tx("foot.noserve")} · ${this.tx("taken.note")}` };
      }
      f.confirm = this.K.can && this.head && t.conf === "pending";
      f.retryConfirm = this.K.can && t.conf === "failed";
      f.retryScan = t.kind === "error";
      f.signin = t.kind === "session";
      f.enterId = this.head && t.kind === "unknown";
      f.advance = this.canAdvance;
      f.details =
        this.outcome.info.length > 0 &&
        (this.showDetails || t.kind === "stop" || t.kind === "unknown");
      return f;
    },
    hasActs() {
      const f = this.foot;
      return f.confirm || f.retryConfirm || f.retryScan || f.signin || f.enterId || f.advance || f.details;
    },
    hasFoot() {
      return !!this.foot.status || this.hasActs;
    },
    label() {
      const nm = this.fullName || (this.ticket.code ? `ID ${this.ticket.code}` : "");
      return [`${this.pos}.`, nm, this.title].filter(Boolean).join(", ");
    },
  },
  methods: {
    imageUrl(it) {
      return `${conf.apiUrlBase}/gallery/${this.restaurantId}/medium/${it.image}`;
    },
    imageOk(it) {
      return !!it.image && !this.failedImages[it.image];
    },
    markFailed(it) {
      this.failedImages = { ...this.failedImages, [it.image]: true };
    },
  },
};
</script>
